/**
 * /app/signals — full signal history page.
 *
 * Filter bar:
 *   symbol · direction · signal_type · timeframe · matched · days
 *
 * Click on a row → SignalDecisionDrawer with the matcher_decisions
 * trace (rejections / passed strategies / matched outcome).
 */
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Pagination } from '@/components/ui/Pagination'
import { SignalChartDrawer } from '@/components/signals/SignalChartDrawer'
import { SignalDecisionDrawer } from '@/components/signals/SignalDecisionDrawer'
import { SignalDetailDrawer } from '@/components/signals/SignalDetailDrawer'
import {
  type HistorySignal,
  useSignalHistory,
} from '@/hooks/useDashboardApi'
import { cn } from '@/lib/cn'
import { formatPct } from '@/lib/format'

const PAGE_SIZE = 50

const SIGNAL_TYPES = [
  'divergence', 'pump_dump', 'super_pump_dump', 'reversal', 'liquidation',
] as const

const TIMEFRAMES = ['5m', '15m', '1h', '4h'] as const

export default function Signals() {
  const { t } = useTranslation('app')

  const [symbol, setSymbol] = useState('')
  const [direction, setDirection] = useState<'' | 'long' | 'short'>('')
  const [signalType, setSignalType] = useState('')
  const [timeframe, setTimeframe] = useState('')
  const [matched, setMatched] = useState<'' | 'yes' | 'no'>('')
  const [days, setDays] = useState(7)
  const [offset, setOffset] = useState(0)

  // PULSE-231: клик по строке открывает график-досье (chart); из него
  // доступны полные метрики (detail) и decision-trace (decision) —
  // один дровер на экране за раз.
  const [chart, setChart] = useState<{ id: number; label: string } | null>(null)
  const [detail, setDetail] = useState<{ id: number; label: string } | null>(null)
  const [decision, setDecision] = useState<{ id: number; label: string } | null>(null)

  const filters = useMemo(() => ({
    limit: PAGE_SIZE,
    offset,
    symbol: symbol.trim().toUpperCase() || undefined,
    direction: direction || undefined,
    signal_type: signalType || undefined,
    timeframe: timeframe || undefined,
    matched: matched === 'yes' ? true : matched === 'no' ? false : undefined,
    days,
  }), [symbol, direction, signalType, timeframe, matched, days, offset])

  const { data, isLoading, isFetching } = useSignalHistory(filters)

  const resetOffset = () => setOffset(0)

  return (
    <div className="p-4 sm:p-6">
      <header className="mb-4">
        <h1 className="text-2xl font-semibold tracking-tight">
          {t('signals.title')}
        </h1>
        <p className="text-sm text-textDim mt-1">{t('signals.subtitle')}</p>
      </header>

      {/* Filter bar */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 mb-3">
        <Input
          placeholder={t('signals.filter.symbol_placeholder')}
          value={symbol}
          onChange={(e) => { setSymbol(e.target.value); resetOffset() }}
        />
        <Select
          value={direction}
          onChange={(e) => {
            setDirection(e.target.value as '' | 'long' | 'short')
            resetOffset()
          }}
        >
          <option value="">{t('positions.filter.direction_all')}</option>
          <option value="long">{t('dashboard.open_positions.side.long')}</option>
          <option value="short">{t('dashboard.open_positions.side.short')}</option>
        </Select>
        <Select
          value={signalType}
          onChange={(e) => { setSignalType(e.target.value); resetOffset() }}
        >
          <option value="">{t('signals.filter.type_all')}</option>
          {SIGNAL_TYPES.map((s) => (
            <option key={s} value={s}>
              {t(`signal_type.${s}`, { defaultValue: s })}
            </option>
          ))}
        </Select>
        <Select
          value={timeframe}
          onChange={(e) => { setTimeframe(e.target.value); resetOffset() }}
        >
          <option value="">{t('signals.filter.tf_all')}</option>
          {TIMEFRAMES.map((tf) => (
            <option key={tf} value={tf}>{tf}</option>
          ))}
        </Select>
        <Select
          value={matched}
          onChange={(e) => {
            setMatched(e.target.value as '' | 'yes' | 'no')
            resetOffset()
          }}
        >
          <option value="">{t('signals.filter.matched_all')}</option>
          <option value="yes">{t('signals.filter.matched_yes')}</option>
          <option value="no">{t('signals.filter.matched_no')}</option>
        </Select>
        <Select
          value={String(days)}
          onChange={(e) => { setDays(Number(e.target.value)); resetOffset() }}
        >
          <option value="1">{t('positions.filter.days', { count: 1 })}</option>
          <option value="7">{t('positions.filter.days', { count: 7 })}</option>
          <option value="30">{t('positions.filter.days', { count: 30 })}</option>
          <option value="90">{t('positions.filter.days', { count: 90 })}</option>
        </Select>
      </div>

      <section className="bg-ink border border-line">
        {isLoading || !data ? (
          <div className="px-5 py-10 text-center text-textFaint text-sm">—</div>
        ) : data.items.length === 0 ? (
          <div className="px-5 py-10 text-center text-textFaint text-sm">
            {t('signals.empty')}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-textDim text-xs uppercase tracking-wider border-b border-line bg-elevated/40">
                  <tr>
                    <Th className="text-left px-5">{t('signals.table.ts')}</Th>
                    <Th className="text-left">{t('signals.table.symbol')}</Th>
                    <Th className="text-left">{t('signals.table.dir')}</Th>
                    <Th className="text-left">{t('signals.table.type')}</Th>
                    <Th className="text-left">{t('signals.table.tf')}</Th>
                    <Th className="text-right">{t('signals.table.dp')}</Th>
                    <Th className="text-right">{t('signals.table.doi')}</Th>
                    <Th className="text-right">{t('signals.table.score')}</Th>
                    <Th className="text-right px-5">{t('signals.table.matched')}</Th>
                  </tr>
                </thead>
                <tbody className="font-mono">
                  {renderRowsWithDayHeaders(
                    data.items,
                    (s) => setChart({
                      id: s.id,
                      label: `${s.symbol} · ${s.signal_type} · ${s.timeframe}`,
                    }),
                    t,
                  )}
                </tbody>
              </table>
            </div>
            <Pagination
              total={data.total}
              limit={data.limit}
              offset={data.offset}
              onChange={setOffset}
              loading={isFetching}
            />
          </>
        )}
      </section>

      <SignalChartDrawer
        signalId={chart?.id ?? null}
        label={chart?.label}
        onClose={() => setChart(null)}
        onOpenDetail={() => {
          if (chart) {
            setDetail(chart)
            setChart(null)
          }
        }}
        onOpenDecision={() => {
          if (chart) {
            setDecision(chart)
            setChart(null)
          }
        }}
      />

      <SignalDetailDrawer
        signalId={detail?.id ?? null}
        onClose={() => setDetail(null)}
        onOpenDecision={() => {
          if (detail) {
            setDecision(detail)
            setDetail(null)
          }
        }}
      />

      <SignalDecisionDrawer
        signalId={decision?.id ?? null}
        signalLabel={decision?.label}
        onClose={() => setDecision(null)}
      />
    </div>
  )
}

/* ─── Rendering helpers ─────────────────────────────────────────── */

type TFn = (key: string, opts?: Record<string, unknown>) => string

/**
 * Renders rows with sticky day-headers (Today / Yesterday / "May 15")
 * + star badge on the top-score signal of each day. Single pass over
 * the already-sorted-desc items: when the day changes, emit a header
 * row; remember which id has the highest score for the current day.
 */
function renderRowsWithDayHeaders(
  items: HistorySignal[],
  onClick: (s: HistorySignal) => void,
  t: TFn,
) {
  // 1) Pre-compute per-day-top-score (single pass; items are reverse-chrono)
  const topByDay = new Map<string, number>()
  for (const s of items) {
    const k = dayKey(s.ts)
    const cur = topByDay.get(k)
    const sc = s.score ?? -Infinity
    if (cur == null || sc > cur) topByDay.set(k, sc)
  }

  // 2) Walk again, emit headers + rows
  const nodes: React.ReactNode[] = []
  let lastDay = ''
  for (const s of items) {
    const k = dayKey(s.ts)
    if (k !== lastDay) {
      lastDay = k
      nodes.push(
        <tr key={`hdr-${k}`}>
          <td colSpan={9}
              className="px-5 py-1.5 text-2xs uppercase tracking-wider
                         text-textDim font-mono
                         border-t border-line bg-elevated/30">
            {dayLabel(s.ts, t)}
          </td>
        </tr>,
      )
    }
    const isTopOfDay = s.score != null && s.score === topByDay.get(k) && s.score >= 52
    nodes.push(
      <SignalRow
        key={s.id}
        sig={s}
        isTopOfDay={isTopOfDay}
        onClick={() => onClick(s)}
      />,
    )
  }
  return nodes
}

function SignalRow({
  sig, isTopOfDay, onClick,
}: {
  sig: HistorySignal
  isTopOfDay: boolean
  onClick: () => void
}) {
  const { t } = useTranslation('app')
  const dirTone = sig.direction === 'long' ? 'text-pulse' : 'text-rose'
  const heat = scoreHeatClass(sig.score)
  const isSuper = sig.signal_type === 'super_pump_dump'
  const isStrong = sig.score != null && sig.score >= 55

  // Heat lives in the score pill + a left accent bar (4px) only.
  // No row-wide tint — that produced visual mush across 50.x rows.
  // Strong signals (score>=55) get a thin fire left-edge + slightly
  // brighter row text. Super-pump_dump always gets the fire edge.
  return (
    <tr
      onClick={onClick}
      className={cn(
        'group border-b border-line/40 hover:bg-elevated/40 cursor-pointer transition-colors relative',
        sig.matched_for_me && 'bg-fire-faint/20',
      )}
      title={new Date(sig.ts).toLocaleString()}
    >
      <td className="px-5 py-3 text-textFaint tabular-nums relative">
        {/* Left accent bar — 3px coloured stripe */}
        {(isStrong || isSuper) && (
          <span
            className="absolute left-0 top-0 bottom-0 w-[3px]"
            style={{ background: isSuper ? 'var(--fire)' : 'var(--amber)' }}
            aria-hidden
          />
        )}
        {timeHHMM(sig.ts)}
      </td>
      <td className="py-3 font-semibold flex items-center gap-1.5">
        {isTopOfDay && <span className="text-amber" title={t('signals.top_of_day')}>★</span>}
        <span className={isStrong || isSuper ? 'text-text' : 'text-textDim'}>
          {sig.symbol}
        </span>
      </td>
      <td className={cn('py-3 uppercase font-semibold', dirTone)}>
        {sig.direction === 'long' ? 'L' : 'S'}
      </td>
      <td className="py-3 text-textDim">
        {isSuper ? (
          <span className="pill bg-fire text-ink text-2xs">SUPER</span>
        ) : (
          <span className="text-xs">
            {t(`signal_type.${sig.signal_type}`, { defaultValue: sig.signal_type })}
          </span>
        )}
      </td>
      <td className="py-3 text-textDim text-xs">{sig.timeframe}</td>
      <td className="text-right py-3 text-textDim tabular-nums">
        {sig.dp_pct != null ? formatPct(sig.dp_pct, { sign: true }) : '—'}
      </td>
      <td className="text-right py-3 text-textDim tabular-nums">
        {sig.doi_pct != null ? formatPct(sig.doi_pct, { sign: true }) : '—'}
      </td>
      <td className="text-right py-3 tabular-nums">
        {sig.score != null ? (
          <span className={cn('pill', heat.pill)}>
            {sig.score.toFixed(1)}
          </span>
        ) : <span className="text-textFaint">—</span>}
      </td>
      <td className="text-right px-5 py-3">
        {sig.matched_for_me ? (
          <span
            className="pill bg-fire-faint text-fire"
            title={t('signals.matched_yes_tooltip') as string}
          >
            {t('signals.matched_pill_yes')}
          </span>
        ) : (
          <span
            className="text-textFaint"
            title={t('signals.matched_no_tooltip') as string}
          >
            —
          </span>
        )}
      </td>
    </tr>
  )
}

function Th({
  className, children,
}: {
  className?: string
  children: React.ReactNode
}) {
  return (
    <th className={cn('py-2 font-medium', className)}>{children}</th>
  )
}

/* ─── time + score utils ────────────────────────────────────────── */

function timeHHMM(ts: string): string {
  const d = new Date(ts)
  const h = String(d.getHours()).padStart(2, '0')
  const m = String(d.getMinutes()).padStart(2, '0')
  return `${h}:${m}`
}

function dayKey(ts: string): string {
  const d = new Date(ts)
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
}

function dayLabel(ts: string, t: TFn): string {
  const d = new Date(ts)
  const today = new Date()
  const yest = new Date(); yest.setDate(today.getDate() - 1)
  if (sameYMD(d, today))  return t('signals.today')
  if (sameYMD(d, yest))   return t('signals.yesterday')
  return d.toLocaleDateString([], { day: '2-digit', month: 'long', year: d.getFullYear() === today.getFullYear() ? undefined : 'numeric' })
}

function sameYMD(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear()
      && a.getMonth() === b.getMonth()
      && a.getDate() === b.getDate()
}

/** Map score → tailwind classes for the score pill. Thresholds from real
 *  distribution (50-58 typical, >60 rare topping). After PULSE-47 the
 *  row no longer carries a heat tint — too noisy across 50.x rows. */
function scoreHeatClass(score: number | null): { pill: string } {
  if (score == null) return { pill: 'bg-elevated text-textDim' }
  if (score >= 60)   return { pill: 'bg-fire text-ink' }
  if (score >= 55)   return { pill: 'bg-amber-faint text-amber' }
  if (score >= 52)   return { pill: 'bg-pulse-faint text-pulse' }
  return { pill: 'bg-elevated text-textDim' }
}
